import type { FullATSScoreV3 } from '@shared/types/ats-report';
import { isFullReport } from '@shared/types/ats-report';

interface ProfilePreviewProps {
  fullName: string;
  jobTitle?: string;
  location?: string;
  skills: string[];
  experience: Array<{
    company: string;
    position: string;
    startDate?: string;
    endDate?: string;
  }>;
  education: Array<{
    institution: string;
    degree: string;
  }>;
  fieldsExtracted: number;
  atsReport?: FullATSScoreV3;
  atsLoading?: boolean;
}

export function ProfilePreviewCard({
  fullName,
  jobTitle,
  location,
  skills,
  experience,
  education,
  fieldsExtracted,
  atsReport,
  atsLoading,
}: ProfilePreviewProps) {
  const initials = fullName
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const metaParts = [jobTitle, location].filter(Boolean).join(' \u00B7 ');
  const isGoodParse = fieldsExtracted >= 6;
  const visibleSkills = skills.slice(0, 6);
  const overflowCount = skills.length - visibleSkills.length;
  const visibleExperience = experience.slice(0, 3);

  const formatPeriod = (start?: string, end?: string) => {
    if (!start) return '';
    return end ? `${start} \u2013 ${end}` : `${start} \u2013 Present`;
  };

  const atsScore = atsReport?.report?.overallScore ?? null;
  const topMissingSkills: string[] =
    atsReport && isFullReport(atsReport.report)
      ? (atsReport.report.sections.skills.missingSkills ?? []).slice(0, 3)
      : [];

  const scoreColor =
    atsScore === null ? '' :
    atsScore >= 80 ? 'var(--success)' :
    atsScore >= 60 ? 'var(--warning)' :
    'var(--danger)';

  return (
    <div className="profile-preview">
      {/* Identity */}
      <div className="profile-preview__identity">
        <div className="profile-preview__avatar">{initials || '?'}</div>
        <div>
          <div className="profile-preview__name">{fullName || 'Your Name'}</div>
          {metaParts && <div className="profile-preview__meta">{metaParts}</div>}
        </div>
      </div>

      {/* Skills */}
      {skills.length > 0 && (
        <>
          <div className="profile-preview__section-label">Skills</div>
          <div className="profile-preview__skills">
            {visibleSkills.map((skill) => (
              <span key={skill} className="profile-preview__skill-chip">
                {skill}
              </span>
            ))}
            {overflowCount > 0 && (
              <span className="profile-preview__skill-chip profile-preview__skill-chip--overflow">
                +{overflowCount}
              </span>
            )}
          </div>
        </>
      )}

      {/* Experience */}
      {visibleExperience.length > 0 && (
        <>
          <div className="profile-preview__divider" />
          <div className="profile-preview__section-label">Experience</div>
          {visibleExperience.map((exp, i) => (
            <div key={i} className="profile-preview__exp-row">
              <div>
                <div className="profile-preview__exp-company">{exp.company}</div>
                <div className="profile-preview__exp-role">{exp.position}</div>
              </div>
              <div className="profile-preview__exp-period">
                {formatPeriod(exp.startDate, exp.endDate)}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Education */}
      {education.length > 0 && (
        <>
          <div className="profile-preview__divider" />
          <div className="profile-preview__section-label">Education</div>
          {education.slice(0, 2).map((edu, i) => (
            <div key={i} className="profile-preview__exp-row">
              <div>
                <div className="profile-preview__exp-company">{edu.institution}</div>
                <div className="profile-preview__exp-role">{edu.degree}</div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Quality badge */}
      <div
        className={`profile-preview__quality-badge ${
          isGoodParse ? 'profile-preview__quality-badge--good' : 'profile-preview__quality-badge--partial'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        {fieldsExtracted} fields extracted
      </div>

      {/* ATS Score section (shows after parallel analysis completes) */}
      {(atsLoading || atsReport) && (
        <div className="profile-preview__ats-section">
          <div className="profile-preview__divider" />
          <div className="profile-preview__section-label">ATS Compatibility</div>

          {atsLoading && !atsReport ? (
            <div className="profile-preview__ats-skeleton">
              <div className="profile-preview__ats-skeleton-score" />
              <div className="profile-preview__ats-skeleton-bar" />
              <p className="profile-preview__ats-skeleton-text">Analyzing ATS compatibility...</p>
            </div>
          ) : atsScore !== null ? (
            <div className="profile-preview__ats-result">
              <div className="profile-preview__ats-score-row">
                <span
                  className="profile-preview__ats-score"
                  style={{ color: scoreColor }}
                >
                  {atsScore}
                </span>
                <span className="profile-preview__ats-score-label">/ 100</span>
              </div>
              {topMissingSkills.length > 0 && (
                <div className="profile-preview__ats-missing">
                  <span className="profile-preview__ats-missing-label">Top gaps: </span>
                  {topMissingSkills.join(', ')}
                </div>
              )}
              <p className="profile-preview__ats-note">
                Add a job description in the dashboard for a job-specific score.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
